/**
 * Brand hub types and helpers (web plan W3.6): the data shapes the SPA page
 * (`src/pages/detail/BrandDetailPage.tsx`) and the SEO server share, so both
 * query the backend's `brandPage` the same way.
 *
 * Pure (no React, no `window`).
 */
import type { Brand, EntitySeo } from '@/types';
import { localeKey, type Locale } from './locales';

/** One entry in a brand's sale history (backend `SaleHistoryEntry`). */
export interface SaleHistoryEntry {
  id: string;
  /** Staff headline, else cleaned name. Nullable per SDL. */
  headline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  discountType?: string | null;
  discountMin?: number | null;
  discountMax?: number | null;
  /**
   * The promotion's page path while it still answers 200 (ended ≤ 90 days
   * ago). Null after — render as plain text, never link to a 410.
   */
  urlPath?: string | null;
}

/** The brand hub's extra data (fields from `brandPage` only). */
export interface BrandHubPage {
  brand: Pick<Brand, 'id' | 'name' | 'urlPath' | 'seo'>;
  /** Ended promotions, most recently ended first (≤ 100). */
  saleHistory: readonly SaleHistoryEntry[];
  /** ≤ 12 other indexable brands sharing a category. */
  similarBrands: readonly Pick<Brand, 'id' | 'name' | 'urlPath' | 'logo' | 'seo'>[];
  /** Publicly visible store count in the page's country. */
  storeCount: number;
  /** The hub's index policy (D-5). */
  seo: EntitySeo;
}

/** Variables the SPA hook and the SEO server pass to `brandPage`. */
export function brandPageHubVariables(
  slug: string,
  locale: Locale,
): { slug: string; locale: string } {
  return { slug, locale: localeKey(locale) };
}

/**
 * Whether the sale history warrants a "next sale" prediction section:
 * ≥ 3 past sales required (plan W3.6).
 */
export function hasEnoughSaleHistory(saleHistory: readonly SaleHistoryEntry[]): boolean {
  return saleHistory.length >= 3;
}
